import React, { useState, useRef, useEffect } from 'react'
import { Send, Bot, User, Loader2, Code, Play, Copy, Edit3, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'

export interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: Date
  type?: 'text' | 'code' | 'execution'
  metadata?: {
    playwrightCode?: string
    executionResult?: any
    sessionId?: string
    executionMethod?: 'cdp' | 'natural-language' | 'stagehand'
  }
}

interface ChatInterfaceProps {
  onSendMessage: (message: string) => Promise<void>
  messages: ChatMessage[]
  isLoading?: boolean
  className?: string
  onExecuteCode?: (code: string) => Promise<void>
  onExecuteCodeViaCDP?: (code: string, sessionId?: string) => Promise<void>
  currentSessionId?: string
}

export function ChatInterface({
  onSendMessage,
  messages,
  isLoading = false,
  className,
  onExecuteCode,
  onExecuteCodeViaCDP,
  currentSessionId
}: ChatInterfaceProps) {
  const [input, setInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingCode, setEditingCode] = useState<string | null>(null)
  const [editedCode, setEditedCode] = useState('')
  const [showCodeEditor, setShowCodeEditor] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const { toast } = useToast()

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || isSubmitting) return

    const message = input.trim()
    setInput('')
    setIsSubmitting(true)

    try {
      await onSendMessage(message)
    } catch (error) {
      console.error('Failed to send message:', error)
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast({
        title: "Copied",
        description: "Code copied to clipboard",
      })
    } catch (error) {
      console.error('Failed to copy:', error)
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive"
      })
    }
  }

  const executeCode = async (code: string) => {
    if (!onExecuteCode) return

    try {
      await onExecuteCode(code)
      toast({
        title: "Executing",
        description: "Code execution started (Natural Language)",
      })
    } catch (error) {
      console.error('Failed to execute code:', error)
      toast({
        title: "Error",
        description: "Failed to execute code",
        variant: "destructive"
      })
    }
  }

  const executeCodeViaCDP = async (code: string) => {
    if (!onExecuteCodeViaCDP) return

    try {
      await onExecuteCodeViaCDP(code, currentSessionId)
      toast({
        title: "Executing",
        description: "Code execution started (Direct CDP)",
      })
    } catch (error) {
      console.error('Failed to execute code via CDP:', error)
      toast({
        title: "Error",
        description: "Failed to execute code via CDP",
        variant: "destructive"
      })
    }
  }

  const openCodeEditor = (code: string) => {
    setEditingCode(code)
    setEditedCode(code)
    setShowCodeEditor(true)
  }

  const saveEditedCode = () => {
    if (editingCode && editedCode) {
      // Update the message with the edited code
      // In a real implementation, this would update the message in the parent component
      toast({
        title: "Code Updated",
        description: "Your changes have been saved",
      })
      setShowCodeEditor(false)
      setEditingCode(null)
    }
  }

  return (
    <Card className={cn("flex flex-col h-full", className)}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <Bot className="h-5 w-5" />
          AI Workflow Assistant
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Describe what you want to automate and I'll generate Playwright code for you
        </p>
      </CardHeader>
      
      <CardContent className="flex-1 flex flex-col p-0">
        {/* Messages */}
        <ScrollArea className="flex-1 px-4">
          <div className="space-y-4 pb-4">
            {messages.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                <Bot className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h4 className="text-lg font-medium mb-2">Ready to help!</h4>
                <p className="text-sm">
                  Describe a web automation task and I'll generate Playwright code for you.
                </p>
                <div className="mt-4 text-xs space-y-1">
                  <p>Examples:</p>
                  <p>"Navigate to Google and search for 'Playwright'"</p>
                  <p>"Fill out a contact form on example.com"</p>
                  <p>"Extract product prices from an e-commerce site"</p>
                </div>
              </div>
            )}
            
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "flex gap-3",
                  message.role === 'user' ? "justify-end" : "justify-start"
                )}
              >
                {message.role === 'assistant' && (
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                )}
                
                <div className={cn(
                  "rounded-lg px-3 py-2 min-w-0 flex-1",
                  message.type === 'code' ? "max-w-full" : "max-w-[80%]",
                  message.role === 'user'
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted"
                )}>
                  {message.type === 'code' && message.metadata?.playwrightCode ? (
                    <div className="space-y-3">
                      <p className="text-sm">{message.content}</p>
                      <div className="bg-background rounded border p-3">
                        <div className="flex flex-col gap-2 mb-2">
                          <div className="flex items-center justify-between">
                            <Badge variant="secondary" className="text-xs flex-shrink-0">
                              <Code className="h-3 w-3 mr-1" />
                              Playwright Code
                            </Badge>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-3 text-xs"
                              onClick={() => copyToClipboard(message.metadata!.playwrightCode!)}
                              title="Copy code"
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              Copy
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-3 text-xs"
                              onClick={() => openCodeEditor(message.metadata!.playwrightCode!)}
                              title="Edit code"
                            >
                              <Edit3 className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                            {(onExecuteCode || onExecuteCodeViaCDP) && (
                              <div className="flex gap-1">
                                {onExecuteCodeViaCDP && (
                                  <Button
                                    size="sm"
                                    variant="default"
                                    className="h-8 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                                    onClick={() => executeCodeViaCDP(message.metadata!.playwrightCode!)}
                                    title="Execute code directly via CDP (Recommended)"
                                  >
                                    <Play className="h-3 w-3 mr-1" />
                                    Execute (CDP)
                                  </Button>
                                )}
                                {onExecuteCode && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 px-3 text-xs"
                                    onClick={() => executeCode(message.metadata!.playwrightCode!)}
                                    title="Execute code via natural language (Fallback)"
                                  >
                                    <Play className="h-3 w-3 mr-1" />
                                    Execute (AI)
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <pre className="text-xs overflow-x-auto">
                          <code>{message.metadata.playwrightCode}</code>
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  )}
                  
                  <div className={cn(
                    "text-xs mt-1 opacity-70",
                    message.role === 'user' ? "text-primary-foreground" : "text-muted-foreground"
                  )}>
                    {message.timestamp.toLocaleTimeString()}
                  </div>
                </div>
                
                {message.role === 'user' && (
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {(isLoading || isSubmitting) && (
              <div className="flex gap-3 justify-start">
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-primary" />
                  </div>
                </div>
                <div className="bg-muted rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm text-muted-foreground">
                      Thinking...
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div ref={messagesEndRef} />
        </ScrollArea>
        
        {/* Input */}
        <div className="border-t p-4">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <Textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Describe what you want to automate..."
              className="min-h-[60px] max-h-[120px] resize-none"
              disabled={isSubmitting}
            />
            <Button
              type="submit"
              size="sm"
              disabled={!input.trim() || isSubmitting}
              className="self-end"
            >
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </form>
        </div>
      </CardContent>

      {/* Code Editor Dialog */}
      <Dialog open={showCodeEditor} onOpenChange={setShowCodeEditor}>
        <DialogContent className="max-w-4xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Edit Playwright Code</DialogTitle>
            <DialogDescription>
              Make changes to the generated code and save your edits.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0">
            <Textarea
              value={editedCode}
              onChange={(e) => setEditedCode(e.target.value)}
              className="min-h-[400px] font-mono text-sm"
              placeholder="// Your Playwright code here..."
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCodeEditor(false)}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={saveEditedCode}>
              <Check className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}
